import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import style from './InquiryV4.module.scss';
import Anchor from '../../common/component/Anchor';
import CheckBoxV4 from '../../common/component/CheckBoxV4';
import ConfirmButtonV4, {
  CONFIRM_BTN_SHAPE_CN_V4,
  CONFIRM_BTN_TEXT_CN_V4,
  DISABLED_CONFIRM_BTN_TEXT_CN_V4,
} from '../../common/component/ConfirmButtonV4';
import DescText from '../../common/component/DescText';
import Text from '../../common/component/Text';
import {IS_CBT_OR_PROD, REGION} from '../../common/constant';
import {Page} from '../../common/constant/page';
import {pToast} from '../../common/container/PToast';
import {getRegexEmail, getRegexNumber} from '../../common/function';
import useUserAgent from '../../common/hook/useUserAgent';
import {IRegion} from '../../common/type';
import {useFetchInfo} from '../../common/util/fetch';
import DropDownV4 from '../../commonSub/component/DropDownV4';
import InputV4 from '../../commonSub/component/InputV4';
import InquiryFileInputV4 from '../component/InquiryFileV4Input';
import MoreContainerV4 from '../component/MoreContainerV4';
import useInquiryFile from '../hook/useInquiryFile';
import {actions, ActionType} from '../state/action';
import {IInquiry} from '../type';

interface IProps {
  isWebview: boolean;
  space?: number;
}

export default function InquiryV4({isWebview, space}: IProps) {
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const userAgent = useUserAgent();
  const {fileRef, fileList, setFileList, onChangeFile, initFileList} =
    useInquiryFile();

  const [selectedInquiryIndex, setSelectedInquiryIndex] = useState<number>();

  const inquiryList = useMemo(
    () =>
      isWebview && userAgent?.isAndroid
        ? INQUIRY_LIST[REGION]
        : INQUIRY_LIST[REGION].map(inquiry =>
            inquiry.key === 'ticket'
              ? {...inquiry, contentsWebview: TICKET_CONTENTS_IOS}
              : inquiry
          ),
    [isWebview, userAgent]
  );

  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [inquiryContents, setInquiryContents] = useState<string>(
    t('more_customer_inquiry_category_detail')
  );

  const [isAgreement, setIsAgreement] = useState(false);

  const {fetchStatus} = useFetchInfo(ActionType.FetchInquiry);

  const content = isWebview ? 'contentsWebview' : 'contents';
  const canWrite = selectedInquiryIndex !== undefined;
  const isKorea = REGION === 'kor';

  useEffect(() => {
    if (fetchStatus === 'success') {
      setEmail('');
      setPhoneNumber('');
      setInquiryContents(t('more_customer_inquiry_category_detail'));
      setIsAgreement(false);
      setFileList(undefined);
      setSelectedInquiryIndex(undefined);
      initFileList();
    }
  }, [fetchStatus, initFileList, setFileList, t]);

  useEffect(() => {
    if (selectedInquiryIndex !== undefined) {
      setInquiryContents(inquiryList[selectedInquiryIndex][content]);
    }
  }, [selectedInquiryIndex, content, t, inquiryList]);

  const onClickWhenCannotWrite = () => {
    if (!canWrite) {
      pToast(t('more_customer_inquiry_send_unselected_toast'));
    }
  };

  const onClickAdd = () => {
    if (canWrite) {
      fileRef.current?.click();
    } else {
      onClickWhenCannotWrite();
    }
  };

  const onClickSend = () => {
    if (!getRegexEmail().test(email)) {
      pToast(t('more_customer_inquiry_answer_email_error'));
      return;
    }

    let categoryId = '';

    if (selectedInquiryIndex !== undefined) {
      if (userAgent?.isWebView) {
        if (userAgent?.isIos) {
          categoryId = t(inquiryList[selectedInquiryIndex]?.iosKey);
        } else {
          categoryId = t(inquiryList[selectedInquiryIndex].aosKey);
        }
      } else {
        categoryId = t(inquiryList[selectedInquiryIndex].webKey);
      }

      dispatch(
        actions.fetchInquiry({
          email,
          phoneNumber,
          inquiryContents,
          fileList,
          inquiryTitle: t(inquiryList[selectedInquiryIndex].title),
          categoryId,
          userAgent,
        })
      );
    }
  };

  const canConfirm = isKorea
    ? isAgreement && phoneNumber.length && email.length
    : email.length;

  return (
    <MoreContainerV4
      isWebview={isWebview}
      wrapperClassName="mt-10 pb-30"
      isShortHeaderGradient
    >
      <Text className="ml-7 s14-regular-grey15">문의 카테고리</Text>
      <DropDownV4
        placeholder={t('customer_inquiry_category_select')}
        isReset={selectedInquiryIndex === undefined}
        options={inquiryList.map(({key, title}) => ({key, value: title}))}
        className="mt-16 h-52"
        textClassName="s14-medium-white light:text-black flex items-center"
        onSelect={dataKey => {
          const selectedIdx = inquiryList.findIndex(
            inquiry => inquiry.key === dataKey
          );
          setSelectedInquiryIndex(selectedIdx);
        }}
      />
      <textarea
        disabled={!canWrite}
        className={cn(style.textArea, TEXT_AREA_CN)}
        rows={10}
        value={inquiryContents}
        placeholder={t('customer_inquiry_guide_enter')}
        onChange={e => setInquiryContents(e.currentTarget.value)}
      />
      {!isWebview && isKorea && (
        <InquiryFileInputV4
          onChangeFile={onChangeFile}
          onClickAdd={onClickAdd}
          setFileList={setFileList}
          initFileList={initFileList}
          canWrite={canWrite}
          fileList={fileList}
          isWebview={isWebview}
          ref={fileRef}
        />
      )}
      <Text className={cn('ml-7 mt-40', getTitleCn(canWrite))}>
        {t('more_customer_inquiry_answer_email')}
      </Text>
      <div onClick={onClickWhenCannotWrite}>
        <InputV4
          disabled={!canWrite}
          type="email"
          value={email}
          className="w-full mt-16"
          inputClassName="w-full s14-medium-white light:text-black"
          placeholder={t('more_customer_inquiry_answer_email')}
          onChange={e => {
            setEmail(e.currentTarget.value);
          }}
        />
      </div>
      {isKorea && (
        <div>
          <Text className={cn('ml-7 mt-40', getTitleCn(canWrite))}>
            {t('more_customer_inquiry_answer_number')}
          </Text>
          <div onClick={onClickWhenCannotWrite}>
            <InputV4
              disabled={!canWrite}
              type="tel"
              value={phoneNumber}
              className="mt-16 w-full"
              inputClassName="w-full s14-medium-white light:text-black"
              placeholder={t('more_customer_inquiry_answer_number')}
              onChange={e => {
                const userInput = e.currentTarget.value;
                if (!userInput.match(getRegexNumber())) {
                  e.preventDefault();
                } else {
                  setPhoneNumber(userInput);
                }
              }}
            />
          </div>
        </div>
      )}
      {isKorea && (
        <div className={cn('mx-7 mt-40')}>
          <Text className={cn(getTitleCn(canWrite))}>
            개인정보 수집 및 이용 동의
          </Text>
          <Text className={cn('mt-20', getDescCn(canWrite))}>
            카카오웹툰은 이용자 문의를 처리하기 위해 다음과 같이 개인정보를 수집
            및 이용하고 있습니다.
          </Text>
          <DescText className={cn('mt-14', getDescCn(canWrite))}>
            (공통) 이메일 주소, 휴대폰 번호, 카카오웹툰 회원번호
          </DescText>
          <DescText className={getDescCn(canWrite)}>
            (캐시/이용권 관련) 결제 수단, 구글 거래 주문 번호
          </DescText>
          <DescText className={getDescCn(canWrite)}>
            (캐시프렌즈 쇼핑하기) 주문 번호
          </DescText>
          <Text className={cn('mt-14', getDescCn(canWrite))}>
            {
              '문의 유형에 따라 광고 식별자(ADID,IDFA), 기기 정보를 추가로 수집할 수 있습니다. 수집된 개인정보는 문의접수일 기준'
            }
            <span className="font-bold text-[14px]">&nbsp;3년간&nbsp;</span>
            {
              '보관 후 파기되며, 유상 캐시 환불 요청에 따른 환불 진행 시 계좌번호, 예금주명, 계좌은행명, 주문번호, 결제수단, 구매한 콘텐츠명은'
            }
            <span className="font-bold text-[14px]">&nbsp;5년&nbsp;</span>
            {'보관 후 파기됩니다.'}
          </Text>
          <Text className={cn('mt-14', getDescCn(canWrite))}>
            {
              '본 개인정보 수집에 대한 동의를 거부하는 경우 문의사항에 대한 답변이 불가능한 점 양해 부탁드립니다.'
            }
          </Text>
          <Text className={cn('mt-14', getDescCn(canWrite))}>
            {'더 자세한 내용에 대해서는 카카오웹툰의'}&nbsp;
            <Anchor
              pageInfo={{
                page: Page.MorePolicyPersonalInformation,
                space,
              }}
              display="inline"
              className="underline"
            >
              개인정보처리방침
            </Anchor>
            {'을 참고하시기 바랍니다.'}
          </Text>
          <div className="h-1 mt-24 mb-27 bg-grey-02 light:bg-grey-03" />
          <CheckBoxV4
            className={cn(
              isAgreement
                ? 's12-regular-white light:text-black'
                : 's12-regular-grey06'
            )}
            checked={isAgreement}
            label={t('more_customer_inquiry_agreement_check')}
            id="agreement"
            imgWidth={20}
            labelDistance={8}
            onChange={() => {
              if (canWrite) {
                setIsAgreement(!isAgreement);
              } else {
                onClickWhenCannotWrite();
              }
            }}
          />
        </div>
      )}
      <ConfirmButtonV4
        actionType={ActionType.FetchInquiry}
        className={CONFIRM_BTN_SHAPE_CN_V4}
        textClassName={
          canConfirm ? CONFIRM_BTN_TEXT_CN_V4 : DISABLED_CONFIRM_BTN_TEXT_CN_V4
        }
        disabled={!canConfirm}
        onClick={onClickSend}
        wrapperClassName="mt-30 lg:-mx-11"
      >
        {t('more_customer_inquiry_send_button')}
      </ConfirmButtonV4>
    </MoreContainerV4>
  );
}

const TICKET_CONTENTS_IOS =
  '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 결제/ 구매 일자 : \n\n• 결제/ 구매 상세 내역 : (금액 또는 구매한 이용권 작품명) \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 설정> 닉네임> 내 정보에서 확인하실 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n애플 인앱 결제건에 대한 환불 문의는 앱스토어 고객센터로 문의 부탁드립니다.\n\n첨부할 파일이 있는 경우 카카오 고객센터(https://cs.kakao.com)> 카카오웹툰> 문의하기를 통해 접수 부탁드립니다.';

const INQUIRY_LIST: {
  [key in IRegion]: IInquiry[];
} = {
  kor: [
    {
      title: '캐시/이용권 관련',
      key: 'ticket',
      contents:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원 번호 : \n\n• 결제/구매 일자 : \n\n• 결제/ 구매 상세 내역 : (금액 또는 구매한 이용권 작품명)\n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 내정보에서 확인하실 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n구글 인앱 결제건에 대한 환불 문의는 구글 플레이스토어 고객센터로 문의 부탁드립니다.\n\n애플 인앱 결제건에 대한 환불 문의는 앱스토어 고객센터로 문의 부탁드립니다.\n\n파일 첨부에 실패하는 경우, 카카오 고객센터(https://cs.kakao.com) > 카카오웹툰> [문의하기] 를 통해 접수 부탁드립니다.',
      contentsWebview:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 결제/ 구매 일자 : \n\n• 결제/ 구매 상세 내역 : (금액 또는 구매한 이용권 작품명) \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 설정> 닉네임> 내 정보에서 확인하실 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n구글 인앱 결제건에 대한 환불 문의는 구글 플레이스토어 고객센터로 문의 부탁드립니다.\n\n첨부할 파일이 있는 경우 카카오 고객센터(https://cs.kakao.com)> 카카오웹툰> 문의하기를 통해 접수 부탁드립니다.',
      ...(IS_CBT_OR_PROD
        ? {webKey: '63388', aosKey: '63411', iosKey: '63412'}
        : {webKey: '80323', aosKey: '80346', iosKey: '80347'}),
    },
    {
      title: '이벤트 관련',
      key: 'cash',
      contents:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 이벤트 명 : \n\n• 이벤트 참여 일시 : \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 : \n홈 화면> 우상단 더보기> 우상단 내정보에서 확인하실 수 있습니다.\n\n이벤트 보상 미지급 문의는 조건 달성 여부 확인이 필요하여 답변이 다소 지연될 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n파일 첨부에 실패하는 경우, 카카오 고객센터(https://cs.kakao.com) > 카카오웹툰> [문의하기] 를 통해 접수 부탁드립니다.',
      contentsWebview:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 이벤트 명 : \n\n• 이벤트 참여 일시 : \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 설정> 닉네임> 내 정보에서 확인하실 수 있습니다.\n\n이벤트 보상 미지급 문의는 조건 달성 여부 확인이 필요하여 답변이 다소 지연될 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n첨부할 파일이 있는 경우 카카오 고객센터(https://cs.kakao.com)> 카카오웹툰> 문의하기를 통해 접수 부탁드립니다.',
      ...(IS_CBT_OR_PROD
        ? {webKey: '63389', aosKey: '63413', iosKey: '63414'}
        : {webKey: '80324', aosKey: '80348', iosKey: '80349'}),
    },
    {
      title: '권리침해/불법 촬영물 등 유통 신고',
      key: 'right',
      contents:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 신고 작품명 : \n\n• 신고 회차 정보 : \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 : \n홈 화면> 우상단 더보기> 우상단 내정보에서 확인하실 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n파일 첨부에 실패하는 경우, 카카오 고객센터(https://cs.kakao.com) > 카카오웹툰> [문의하기] 를 통해 접수 부탁드립니다.',
      contentsWebview:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 신고 작품명 : \n\n• 신고 회차 정보 : \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 설정> 닉네임> 내 정보에서 확인하실 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n첨부할 파일이 있는 경우 카카오 고객센터(https://cs.kakao.com)> 카카오웹툰> 문의하기를 통해 접수 부탁드립니다.',
      ...(IS_CBT_OR_PROD
        ? {webKey: '63390', aosKey: '63415', iosKey: '63416'}
        : {webKey: '80325', aosKey: '80350', iosKey: '80351'}),
    },
    {
      title: '서비스 이용 관련',
      key: 'common',
      contents:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 내정보에서 확인하실 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n파일 첨부에 실패하는 경우, 카카오 고객센터(https://cs.kakao.com) > 카카오웹툰> [문의하기] 를 통해 접수 부탁드립니다.',
      contentsWebview:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 문의 사항 : \n\n• 카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 설정> 닉네임> 내 정보에서 확인하실 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n첨부할 파일이 있는 경우 카카오 고객센터(https://cs.kakao.com)> 카카오웹툰> 문의하기를 통해 접수 부탁드립니다.',
      ...(IS_CBT_OR_PROD
        ? {webKey: '63391', aosKey: '63417', iosKey: '63418'}
        : {webKey: '80326', aosKey: '80352', iosKey: '80353'}),
    },
    {
      title: '개선 제안',
      key: 'improvement',
      contents:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 :\n\n• 제안 내용 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 내정보에서 확인하실 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n파일 첨부에 실패하는 경우, 카카오 고객센터(https://cs.kakao.com) > 카카오웹툰> [문의하기] 를 통해 접수 부탁드립니다.',
      contentsWebview:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 제안 내용 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 설정> 닉네임> 내 정보에서 확인하실 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n첨부할 파일이 있는 경우 카카오 고객센터(https://cs.kakao.com)> 카카오웹툰> 문의하기를 통해 접수 부탁드립니다.',
      ...(IS_CBT_OR_PROD
        ? {webKey: '63392', aosKey: '63419', iosKey: '63420'}
        : {webKey: '80327', aosKey: '80354', iosKey: '80355'}),
    },
    {
      title: '이용 장애',
      key: 'service',
      contents:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 문제 발생 일시 : \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 내정보에서 확인하실 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n파일 첨부에 실패하는 경우, 카카오 고객센터(https://cs.kakao.com) > 카카오웹툰> [문의하기] 를 통해 접수 부탁드립니다.',
      contentsWebview:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 문제 발생 일시 : \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 설정> 닉네임> 내 정보에서 확인하실 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n첨부할 파일이 있는 경우 카카오 고객센터(https://cs.kakao.com)> 카카오웹툰> 문의하기를 통해 접수 부탁드립니다.',
      ...(IS_CBT_OR_PROD
        ? {webKey: '63393', aosKey: '63421', iosKey: '63422'}
        : {webKey: '80328', aosKey: '80356', iosKey: '80357'}),
    },
    {
      title: '콘텐츠 관련',
      key: 'auth',
      contents:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 작품명 : \n\n• 회차 정보 : \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 내정보에서 확인하실 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n파일 첨부에 실패하는 경우, 카카오 고객센터(https://cs.kakao.com) > 카카오웹툰> [문의하기] 를 통해 접수 부탁드립니다.',
      contentsWebview:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 작품명 : \n\n• 회차 정보 : \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 설정> 닉네임> 내 정보에서 확인하실 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n첨부할 파일이 있는 경우 카카오 고객센터(https://cs.kakao.com)> 카카오웹툰> 문의하기를 통해 접수 부탁드립니다.',
      ...(IS_CBT_OR_PROD
        ? {webKey: '63394', aosKey: '63423', iosKey: '63424'}
        : {webKey: '80329', aosKey: '80358', iosKey: '80359'}),
    },
    {
      title: '캐시프렌즈',
      key: 'cashFriends',
      contents:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 이벤트 명 : \n\n• 이벤트 참여 일시 : \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 내정보에서 확인하실 수 있습니다.\n\n캐시 미지급 문의는 광고주를 통한 확인이 필요하여 답변이 다소 지연될 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n파일 첨부에 실패하는 경우, 카카오 고객센터(https://cs.kakao.com) > 카카오웹툰> [문의하기] 를 통해 접수 부탁드립니다.',
      contentsWebview:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 이벤트 명 : \n\n• 이벤트 참여 일시 : \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 설정> 닉네임> 내 정보에서 확인하실 수 있습니다.\n\n캐시 미지급 문의는 광고주를 통한 확인이 필요하여 답변이 다소 지연될 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n첨부할 파일이 있는 경우 카카오 고객센터(https://cs.kakao.com)> 카카오웹툰> 문의하기를 통해 접수 부탁드립니다.',
      ...(IS_CBT_OR_PROD
        ? {webKey: '63395', aosKey: '63425', iosKey: '63426'}
        : {webKey: '80330', aosKey: '80360', iosKey: '80361'}),
    },
    {
      title: '캐시프렌즈(쇼핑하기)',
      key: 'cashFriendsShopping',
      contents:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 구매한 상품명 : \n\n• 상품 구매 일시 : \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 내정보에서 확인하실 수 있습니다.\n\n캐시 미지급 문의는 광고주를 통한 확인이 필요하여 답변이 다소 지연될 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n파일 첨부에 실패하는 경우, 카카오 고객센터(https://cs.kakao.com) > 카카오웹툰> [문의하기] 를 통해 접수 부탁드립니다.',
      contentsWebview:
        '아래 내용을 입력해주시면 보다 원활한 상담이 가능합니다.\n\n• 카카오웹툰 회원번호 : \n\n• 구매한 상품명 : \n\n• 상품 구매 일시 : \n\n• 문의 사항 : \n\n카카오웹툰 회원번호 확인 경로 :\n홈 화면> 우상단 더보기> 우상단 설정> 닉네임> 내 정보에서 확인하실 수 있습니다.\n\n캐시 미지급 문의는 광고주를 통한 확인이 필요하여 답변이 다소 지연될 수 있습니다.\n\n상담 시 추가 정보를 요청드릴 수 있는 점 참고 부탁드립니다.\n\n첨부할 파일이 있는 경우 카카오 고객센터(https://cs.kakao.com)> 카카오웹툰> 문의하기를 통해 접수 부탁드립니다.',
      ...(IS_CBT_OR_PROD
        ? {webKey: '63396', aosKey: '63427', iosKey: '63428'}
        : {webKey: '80331', aosKey: '80362', iosKey: '80363'}),
    },
  ],
  // TODO: 다른 리전들 기획 나오면 처리한다.
  twn: [],
  tha: [],
  idn: [],
};

const TEXT_AREA_CN = cn(
  'appearance-none shadow-none outline-none resize-none border-none',
  'bg-grey-01 light:bg-white',
  'leading-20 px-18 py-16 w-full h-[172px]',
  'mt-8 rounded-8 s14-medium-white light:text-black',
  'read-only:border read-only:border-solid read-only:border-grey-02 read-only:light:border-grey-03',
  'disabled:!text-grey-03 disabled:!placeholder-grey-03 disabled:!bg-transparent'
);

const getTitleCn: (canWrite: boolean) => string = canWrite =>
  canWrite ? 's14-regular-grey10' : 's14-regular-grey03';

const getDescCn: (canWrite: boolean) => string = canWrite =>
  canWrite ? 's12-regular-grey06' : 's12-regular-grey06 opacity-50';
